import { useCallback, useMemo } from 'react';
import {
  InfiniteData,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query';
import api from 'api';
import { LeadNotification } from 'models/LeadNotifications';
import { NOTIFICATIONS_LIMIT } from 'models/Notification';
import { PaginatedData } from 'models/PaginatedData';
import { getNextPageParam } from 'queryUtils';
import utils, { getHasElementsInArray } from 'utils';
import { SearchableFilterValue } from 'models/Filters';
import { Option } from 'models/Global';

const getParams = (key: string, param: (string | Option)[]) =>
  getHasElementsInArray(param)
    ? {
        [key]: utils.mapArrayToParams(
          utils.multiSelectValueToStringArray(param as any),
        ),
      }
    : {};

const LEAD_NOTIFICATIONS_QUERY_KEY = 'lead-notifications';

type UseLeadNotificationsQueryParams = {
  channel?: string[];
  status?: string[];
  leads?: SearchableFilterValue;
  deals?: SearchableFilterValue;
  enabled?: boolean;
};

export default function useLeadNotificationsQuery({
  channel,
  status,
  leads,
  deals,
  enabled = true,
}: UseLeadNotificationsQueryParams) {
  const queryClient = useQueryClient();

  const invalidate = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: [LEAD_NOTIFICATIONS_QUERY_KEY],
      }),
    [queryClient],
  );

  const updateNotifications = useCallback(
    (updater: (items: LeadNotification[]) => LeadNotification[]) => {
      queryClient.setQueriesData<InfiniteData<PaginatedData<LeadNotification>>>(
        { queryKey: [LEAD_NOTIFICATIONS_QUERY_KEY] },
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              items: updater(page.items),
            })),
          };
        },
      );
    },
    [queryClient],
  );

  const queryKeyArray = useMemo(
    () => [
      LEAD_NOTIFICATIONS_QUERY_KEY,
      {
        ...(getHasElementsInArray(channel) && { channel }),
        ...(getHasElementsInArray(status) && { status }),
        ...(getHasElementsInArray(leads as Option[]) && { leads }),
        ...(getHasElementsInArray(deals as Option[]) && { deals }),
      },
    ],
    [channel, deals, leads, status],
  );

  const query = useInfiniteQuery<PaginatedData<LeadNotification>>({
    queryKey: queryKeyArray,
    queryFn: async ({ pageParam = 1 }) => {
      const { data } = await api.leadNotifications.getNotifications({
        $page: pageParam.toString(),
        $limit: `${NOTIFICATIONS_LIMIT}`,
        $sort: '-createdAt',
        ...getParams('channel', channel),
        ...getParams('status', status),
        ...getParams('buyerIds', leads as Option[]),
        ...getParams('dealRoomIds', deals as Option[]),
      });

      return data;
    },
    enabled,
    getNextPageParam,
  });

  return { query, invalidate, updateNotifications };
}
